import { useStore } from "hooks/use-store";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isCanvasSupported, smoothPath, opacity } from "utils/canvas";
import { fuelViewColors, useFuelStatistics } from "hooks/use-fuel-statistics";
import { useAnimatedValue } from "hooks/use-animated-value";
import { GraphDimensions, GraphMargins } from "types/graph";
import { Color } from "utils/theme";
import {
  calculateXAxisTicks,
  calculateYAxisTicks,
  drawAxes,
  drawAxesBars,
  drawAxesLines,
  drawClippingMask,
  drawDangerZone,
  drawEcoLine,
  drawEcoRanges,
  drawFrame,
  drawFuelCurve,
  drawFuelCurveArea,
  drawGrid,
  drawSafetyLine,
  drawTargetFuelRateLine,
} from "./draw";

const graphVisibility = {
  axes: true,
  axesBars: false,
  axesLines: true,
  grid: true,
  frame: true,
  fuelCurve: true,
  fuelCurveArea: true,
  ecoRanges: false,
  ecoLine: false,
  ecoLineLabel: false,
  safetyLine: true,
  safetyLineLabel: false,
  dangerZone: false,
  targetFuelRate: false,
};

const graphColors = {
  SOG: fuelViewColors.SOG,
  EcoSOG: fuelViewColors.EcoSOG,
  FuelRate: fuelViewColors.FuelRateNmi,
  FuelCurve: Color.Indigo[400],
  Bad: fuelViewColors.Bad,
  Good: fuelViewColors.Good,
};

interface FuelCurveGraphProps {
  shadeUnsafeSpeed?: boolean;
}

export const FuelCurveGraph = ({
  shadeUnsafeSpeed = false,
}: FuelCurveGraphProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "fuel_curve" });

  const {
    MIN_SAIL_SOG,
    ecoSogSpeed,
    avgFuelRateNmi: avgFuelRateNmiRaw,
    fuelRateWarnThreshold,
    isInEcoRange,
    isSogUnsafe,
    isFuelRateBad,
    safetyLimitLabel,
    hasTripStarted,
    maxFuelRateInCurve,
  } = useFuelStatistics();

  const refCanvasWrap = useRef<HTMLDivElement>(null);

  const config = useStore((state) => state.config);
  const liveSogRaw = useStore((state) => state.sog);
  const safetySpeed = useStore((state) => state.rog);
  const ecoSogData = useStore((state) => state.ecoSog);
  const [maxSeenFuelRate, setMaxSeenFuelRate] = useState<number>(0);

  const [context, setContext] = useState<CanvasRenderingContext2D>();

  // Animated values (for fast browsers)
  const liveSog = useAnimatedValue(liveSogRaw);
  const avgFuelRateNmi = useAnimatedValue(avgFuelRateNmiRaw || 0);

  // Determine maximum SOG (x-axis)
  const maxSog = Math.max(
    liveSog + 2,
    config?.max_speed || 20,
    ecoSogData?.fuel_curve_stats?.stable_fuel_curve_sog_range?.[1] || 0
  );

  // Smooth the fuel curve ghost path
  const smoothFuelGhosts = useMemo(() => {
    const MIN_SAIL_SOGForYScaling = Math.min(MIN_SAIL_SOG + 2, liveSogRaw ?? 0);

    const hasFuelGhostData =
      ecoSogData?.fuel_ghost && ecoSogData?.fuel_ghost.upper.length > 0;
    if (!hasFuelGhostData) {
      return null;
    }

    // Smooth the fuel curve ghost path
    const smoothFuelGhostLower = smoothPath(ecoSogData?.fuel_ghost?.lower, 5);
    const smoothFuelGhostUpper = smoothPath(ecoSogData?.fuel_ghost?.upper, 5);

    // Determine maximum fuel rate in the smoothed fuel curve ghost
    const maxFuelGhostRate = Math.max(
      ...smoothFuelGhostUpper
        .filter((p) => p.x >= MIN_SAIL_SOGForYScaling)
        .map((p) => p.y)
    );

    return { smoothFuelGhostLower, smoothFuelGhostUpper, maxFuelGhostRate };
  }, [ecoSogData?.fuel_ghost, liveSogRaw]);

  // Determine maximum fuel rate (y-axis)
  const maxFuelRateRaw = useMemo(() => {
    let maxValue = 1.5; // Fallback value

    const curveMaxRate =
      smoothFuelGhosts && smoothFuelGhosts.maxFuelGhostRate
        ? smoothFuelGhosts.maxFuelGhostRate * 1.2
        : maxFuelRateInCurve;
    if (curveMaxRate) {
      if (config?.target_fuel_rate) {
        // Use target_fuel_rate if set in config and it's higher than the fuel curve maximum
        maxValue = Math.max(curveMaxRate, config.target_fuel_rate + 0.5);
      } else {
        // Otherwise, use the maximum value from the fuel curve
        maxValue = curveMaxRate;
      }
    }

    if (ecoSogData?.fuel_curve_stats?.state !== "ok" && liveSog > 3) {
      // If we don't have reliable fuel curve data, then use the current fuel rate
      // and remember the max value
      if (avgFuelRateNmi) {
        maxValue = Math.max(avgFuelRateNmi, maxSeenFuelRate) * 1.2;
        if (avgFuelRateNmi > maxSeenFuelRate) {
          setMaxSeenFuelRate(avgFuelRateNmi);
        }
      }
    }

    return maxValue;
  }, [
    liveSog,
    avgFuelRateNmi,
    ecoSogData?.fuel_curve_stats,
    maxFuelRateInCurve,
    config,
    smoothFuelGhosts,
  ]);

  // Determine minimum fuel rate (l/nm) (y-axis)
  const minFuelRateRaw = useMemo(() => {
    let minValue = 0.0; // Fallback value

    if (ecoSogData?.fuel_curve_stats?.min_fuel_rate_per_nm) {
      minValue = ecoSogData?.fuel_curve_stats?.min_fuel_rate_per_nm;
    }

    if (config?.target_fuel_rate) {
      minValue = Math.min(minValue, config.target_fuel_rate - 0.5);
    }

    return minValue;
  }, [ecoSogData?.fuel_curve_stats?.min_fuel_rate_per_nm, config]);

  const maxFuelRate = useAnimatedValue(maxFuelRateRaw);
  const minFuelRate = useAnimatedValue(minFuelRateRaw);

  const initCanvas = () => {
    if (!refCanvasWrap.current) return;

    if (!isCanvasSupported()) {
      console.error("Canvas not supported");
      return;
    }

    // Clear canvas
    refCanvasWrap.current.innerHTML = "";

    // Create canvas
    const canvas = document.createElement("canvas");
    refCanvasWrap.current.appendChild(canvas);
    canvas.width = refCanvasWrap.current.clientWidth;
    canvas.height = refCanvasWrap.current.clientHeight;
    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    setContext(ctx);
  };

  const updateCanvas = useCallback(() => {
    const ctx = context;

    if (!ctx) return;

    const { width, height } = ctx.canvas;

    const bigScreen = height > 500;
    const axisTextSizePx = Math.min(Math.max(16, height / 28), 30);

    const margins: GraphMargins = {
      top: 5,
      bottom: axisTextSizePx + 25,
      left: axisTextSizePx * 2.7 + 5,
      right: 5,
    };

    const graphDimensions: GraphDimensions = {
      width: width - margins.left - margins.right,
      height: height - margins.top - margins.bottom,
      top: margins.top,
      left: margins.left,
      bottom: margins.top + height - margins.top - margins.bottom,
      right: margins.left + width - margins.left - margins.right,
    };

    const fuelRateNmi = avgFuelRateNmi || 0;

    // Axis bounds
    const maxSogGraph = Math.max(maxSog, liveSog + 2); // max x-value
    const maxFuelRateGraph =
      liveSog > MIN_SAIL_SOG
        ? Math.max(maxFuelRate, fuelRateNmi * 1.1)
        : maxFuelRate; // max y-value
    const minFuelRateGraph =
      liveSog > MIN_SAIL_SOG
        ? Math.min(minFuelRate, fuelRateNmi * 0.9)
        : minFuelRate;
    const fuelRateRange = maxFuelRateGraph - minFuelRateGraph;
    const exceedingLimits = Boolean(isSogUnsafe || isFuelRateBad);

    // Convert SOG to x-coordinate
    const sogToX = (sog: number) => {
      return (
        graphDimensions.left +
        (graphDimensions.width * (sog - MIN_SAIL_SOG)) /
          (maxSogGraph - MIN_SAIL_SOG)
      );
    };

    // Convert fuel rate to y-coordinate
    const fuelRateToY = (fuelRate: number) => {
      return (
        graphDimensions.bottom -
        (graphDimensions.height * (fuelRate - minFuelRateGraph)) / fuelRateRange
      );
    };

    // Current x and y coordinates
    const fuelRateY = fuelRateToY(fuelRateNmi ?? 0); // y-value
    const sogX = sogToX(liveSog ?? 0); // x-value
    const maxFuelRateY = fuelRateToY(config?.target_fuel_rate ?? 0);
    const ecoSogX = sogToX(ecoSogSpeed ?? 0);
    const safetySpeedX = sogToX(safetySpeed ?? 0);

    const showSafetyLimit = safetySpeed && safetySpeed < maxSogGraph;
    const safetyRegionX =
      shadeUnsafeSpeed && showSafetyLimit
        ? safetySpeedX
        : graphDimensions.right;

    // Clear canvas before drawing
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    // Draw axes bars
    if (hasTripStarted && graphVisibility.axesBars) {
      drawAxesBars(ctx, sogX, fuelRateY, graphDimensions, margins, {
        xAxis: { color: graphColors.SOG },
        yAxis: { color: graphColors.FuelRate },
      });
    }

    // Determine x-axes ticks
    const { xAxisTicks } = calculateXAxisTicks(
      MIN_SAIL_SOG,
      maxSogGraph,
      sogToX
    );

    // Determine y-axes ticks
    const { yAxisTicks, hasFractionalTicks } = calculateYAxisTicks(
      minFuelRateGraph,
      maxFuelRateGraph,
      graphDimensions,
      fuelRateToY
    );

    // Draw axes (ticks and labels)
    if (graphVisibility.axes) {
      drawAxes(ctx, xAxisTicks, yAxisTicks, graphDimensions, {
        fontSize: axisTextSizePx,
        xAxis: { color: graphColors.SOG },
        yAxis: {
          color: graphColors.FuelRate,
          range: fuelRateRange,
          hasFractionalTicks,
        },
      });
    }

    // Draw gridlines
    if (graphVisibility.grid) {
      drawGrid(ctx, xAxisTicks, yAxisTicks, graphDimensions);
    }

    // Draw graph frame (border)
    if (graphVisibility.frame) {
      drawFrame(ctx, graphDimensions);
    }

    // Create a clipping region for the inner graph
    drawClippingMask(
      ctx,
      graphDimensions.left,
      graphDimensions.right,
      graphDimensions.top,
      graphDimensions.bottom
    );

    if (graphVisibility.fuelCurveArea) {
      drawFuelCurveArea(
        ctx,
        smoothFuelGhosts,
        opacity(graphColors.FuelCurve, 0.15),
        sogToX,
        fuelRateToY
      );
    }

    // Draw fuel curve line
    if (graphVisibility.fuelCurve) {
      drawFuelCurve(
        ctx,
        ecoSogData,
        graphColors.FuelCurve,
        bigScreen ? 4 : 3,
        maxSogGraph,
        sogToX,
        fuelRateToY,
        graphDimensions,
        safetySpeed,
        Color.Rose[600],
        graphColors.Good
      );
    }

    // // Draw Eco ranges
    if (graphVisibility.ecoRanges) {
      drawEcoRanges(
        ctx,
        ecoSogData?.eco_ranges,
        sogToX,
        opacity(graphColors.EcoSOG, 0.08),
        graphDimensions,
        safetySpeed
      );
    }
    const ecoSpeedOverlapsSafetySpeed =
      ecoSogSpeed && safetySpeed && Math.abs(ecoSogSpeed - safetySpeed) < 0.5;

    // Draw Eco-SOG line
    if (
      graphVisibility.ecoLine &&
      ecoSogSpeed &&
      !ecoSpeedOverlapsSafetySpeed &&
      !(shadeUnsafeSpeed && safetySpeed && ecoSogSpeed > safetySpeed)
    ) {
      drawEcoLine(
        ctx,
        sogToX(ecoSogSpeed),
        graphDimensions.top,
        t("eco_speed"),
        graphColors.EcoSOG,
        graphDimensions,
        { fontSize: axisTextSizePx, showLabel: graphVisibility.ecoLineLabel }
      );
    }

    // Draw safety speed line
    if (graphVisibility.safetyLine && !shadeUnsafeSpeed && safetySpeed) {
      drawSafetyLine(
        ctx,
        sogToX(safetySpeed),
        safetyLimitLabel,
        graphColors.Bad,
        graphDimensions,
        { fontSize: axisTextSizePx, showLabel: graphVisibility.safetyLineLabel }
      );
    }

    // Draw danger zone
    if (graphVisibility.dangerZone) {
      drawDangerZone(
        ctx,
        graphDimensions,
        graphColors.Bad,
        maxSogGraph,
        fuelRateWarnThreshold,
        fuelRateToY,
        sogToX,
        safetySpeed,
        maxFuelRateGraph,
        safetyLimitLabel,
        axisTextSizePx,
        hasTripStarted,
        shadeUnsafeSpeed,
        exceedingLimits,
        graphVisibility.safetyLineLabel
      );
    }

    // Draw target fuel rate line
    if (graphVisibility.targetFuelRate && config?.target_fuel_rate) {
      drawTargetFuelRateLine(
        ctx,
        t("target_fuel_rate"),
        graphDimensions,
        safetySpeedX,
        ecoSogX,
        safetyRegionX,
        maxFuelRateY
      );
    }

    if (graphVisibility.axesLines) {
      drawAxesLines(
        ctx,
        graphDimensions,
        sogX,
        fuelRateY,
        exceedingLimits,
        Boolean(isInEcoRange && !isSogUnsafe),
        graphColors
      );
    }

    // Disable clipping region
    ctx.restore();
  }, [
    liveSog,
    avgFuelRateNmi,
    fuelRateWarnThreshold,
    ecoSogData,
    ecoSogSpeed,
    safetySpeed,
    maxSog,
    maxFuelRate,
    minFuelRate,
    context,
    config,
    isInEcoRange,
    isSogUnsafe,
    isFuelRateBad,
    safetyLimitLabel,
    hasTripStarted,
    smoothFuelGhosts,
    shadeUnsafeSpeed,
    graphVisibility,
  ]);

  // Init canvas on component mount
  useEffect(() => {
    initCanvas();

    // Reinit canvas on window resize
    window.addEventListener("resize", initCanvas);

    return () => window.removeEventListener("resize", initCanvas);
  }, []);

  // Redraw canvas on data change
  useEffect(() => {
    updateCanvas();
  }, [updateCanvas]);

  return (
    <div
      ref={refCanvasWrap}
      className="w-full h-full flex justify-center items-center font-medium text-3xl"
    >
      {t("loading_fuel_curves")}
    </div>
  );
};
